import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { Radio } from '@atlaskit/radio';
import { Checkbox } from '@atlaskit/checkbox';
import Select from '@atlaskit/select';
import {
  FormValues,
  ChampionPreference,
  YesNo,
  YesNoUnsure,
  TrainingSource,
} from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  howContributeOnline?: string;
  attendedAce?: string;
  aceExperience?: string;
  fourEventsPerYear?: string;
  activityAndEvent?: string;
  whyToCreateContent?: string;
  contentSharingFrequency?: string;
  bestContent1?: string;
  bestContent2?: string;
  bestContent3?: string;
  primarySocialProfile?: string;
  commitSharingContent?: string;
  trainingFrequency?: string;
  trainingSources?: string;
  activeCug?: string;
  eventsPerYear?: string;
  discussInCoE?: string;
  coeFocus?: string;
  otherExcellence?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

type SelectOption = { label: string; value: string };

export const PreferenceDetailsSection: React.FC<Props> = ({ values, updateField, errors }) => {
  const pref = values.championPreference;

  if (!pref) {
    return null;
  }

  // ---- Helpers -------------------------------------------------------------

  const renderYesNoRadios = (field: keyof FormValues, current: YesNo) => (
    <div css={controlTopStyles}>
      <Radio
        name={String(field)}
        label="Yes"
        value="yes"
        isChecked={current === 'yes'}
        onChange={() => updateField(field, 'yes' as any)}
      />
      <Radio
        name={String(field)}
        label="No"
        value="no"
        isChecked={current === 'no'}
        onChange={() => updateField(field, 'no' as any)}
      />
    </div>
  );

  const renderYesNoUnsureRadios = (field: keyof FormValues, current: YesNoUnsure) => (
    <div css={controlTopStyles}>
      <Radio
        name={String(field)}
        label="Yes"
        value="yes"
        isChecked={current === 'yes'}
        onChange={() => updateField(field, 'yes' as any)}
      />
      <Radio
        name={String(field)}
        label="No"
        value="no"
        isChecked={current === 'no'}
        onChange={() => updateField(field, 'no' as any)}
      />
      <Radio
        name={String(field)}
        label="I'm not sure yet"
        value="unsure"
        isChecked={current === 'unsure'}
        onChange={() => updateField(field, 'unsure' as any)}
      />
    </div>
  );

  const renderSelect = <K extends keyof FormValues>(opts: {
    id: string;
    label: string;
    field: K;
    value: FormValues[K] extends string | null ? FormValues[K] : never;
    options: SelectOption[];
    error?: string;
  }) => {
    const { id, label, field, value, options, error } = opts;
    const selectedOption = options.find((o) => o.value === (value as string)) || null;

    return (
      <div css={fieldStyles}>
        <label css={labelStyles} htmlFor={id}>
          {label}
        </label>
        <Select
          inputId={id}
          options={options.filter((o) => o.value !== '')}
          placeholder="Select one"
          value={selectedOption && selectedOption.value ? selectedOption : null}
          onChange={(option) => updateField(field, (option?.value ?? '') as FormValues[K])}
          isClearable
        />
        {error && <div css={errorStyles}>{error}</div>}
      </div>
    );
  };

  const toggleTrainingSource = (source: TrainingSource) => {
    const current = values.trainingSources || [];
    const exists = current.includes(source);
    const next = exists
      ? (current.filter((s) => s !== source) as TrainingSource[])
      : ([...current, source] as TrainingSource[]);
    updateField('trainingSources', next);
  };

  const renderTrainingSources = () => (
    <div css={fieldTopStyles}>
      <fieldset>
        <legend css={labelStyles}>
          What does your organization currently use for Atlassian training? Select all that apply.
        </legend>

        <div css={controlTopStyles}>
          <Checkbox
            name="training_sources"
            label="Atlassian On-Demand Learning"
            isChecked={values.trainingSources.includes('atlassian_on_demand')}
            onChange={() => toggleTrainingSource('atlassian_on_demand')}
          />
          <Checkbox
            name="training_sources"
            label="Solution Partner-provided training"
            isChecked={values.trainingSources.includes('solution_partner')}
            onChange={() => toggleTrainingSource('solution_partner')}
          />
          <Checkbox
            name="training_sources"
            label="In-house training delivered by your organization"
            isChecked={values.trainingSources.includes('in_house_training')}
            onChange={() => toggleTrainingSource('in_house_training')}
          />
          <Checkbox
            name="training_sources"
            label="I create my own training"
            isChecked={values.trainingSources.includes('self_created')}
            onChange={() => toggleTrainingSource('self_created')}
          />
          <Checkbox
            name="training_sources"
            label="Other"
            isChecked={values.trainingSources.includes('other')}
            onChange={() => toggleTrainingSource('other')}
          />
        </div>

        {errors?.trainingSources && <div css={errorStyles}>{errors.trainingSources}</div>}
      </fieldset>
    </div>
  );

  // ---- Conditional content by ChampionPreference ---------------------------

  switch (pref as ChampionPreference) {
    case 'community_forums_contributor':
      return (
        <fieldset css={sectionBoxStyles}>
          <legend css={legendLabelStyles}>
            Contributing to Atlassian Community Forums
          </legend>

          <details css={detailsStyles}>
            <summary css={summaryStyles}>Am I eligible?</summary>
            <p css={detailsContentStyles}>
              To be eligible, you must be an active member of Atlassian Community Forums and have achieved{' '}
              <a
                href="https://community.atlassian.com/forums/Community-Announcements-articles/How-to-become-a-Rising-Star/ba-p/2130775"
                target="_blank"
                rel="noopener noreferrer"
              >
                Rising Star status
              </a>
              .
            </p>
          </details>

          <div css={fieldStyles}>
            <label css={labelStyles}>
              How do you see yourself contributing to the Community Forums?
            </label>
            <TextArea
              id="how-contribute-online"
              value={values.howContributeOnline}
              onChange={(e) => updateField('howContributeOnline', e.target.value)}
            />
            {errors?.howContributeOnline && <div css={errorStyles}>{errors.howContributeOnline}</div>}
          </div>
        </fieldset>
      );

    case 'community_event_organizer':
      return (
        <fieldset css={sectionBoxStyles}>
          <legend css={legendLabelStyles}>
            Organizing Atlassian Community Events
          </legend>

          <details css={detailsStyles}>
            <summary css={summaryStyles}>Am I eligible?</summary>
            <div css={detailsContentStyles}>
              <p>To be eligible, you must:</p>
              <ul css={listStyles}>
                <li>Have attended at least one Atlassian Community Event</li>
                <li>Commit to host at least one event per quarter (90 days), virtually or in person</li>
              </ul>
            </div>
          </details>

          <div css={fieldTopStyles}>
            <label css={labelStyles}>
              Have you previously attended an Atlassian Community Event?
            </label>
            {renderYesNoRadios('attendedAce', values.attendedAce)}
            {errors?.attendedAce && <div css={errorStyles}>{errors.attendedAce}</div>}
          </div>

          {values.attendedAce === 'yes' && (
            <div css={fieldTopStyles}>
              <label css={labelStyles}>
                Please tell us about your experience.
              </label>
              <TextArea
                id="ace-experience"
                value={values.aceExperience}
                onChange={(e) => updateField('aceExperience', e.target.value)}
              />
              {errors?.aceExperience && <div css={errorStyles}>{errors.aceExperience}</div>}
            </div>
          )}

          <div css={fieldTopStyles}>
            <label css={labelStyles}>Can you commit to host at least one event every 90 days?</label>
            {renderYesNoUnsureRadios('fourEventsPerYear', values.fourEventsPerYear as YesNoUnsure)}
            {errors?.fourEventsPerYear && <div css={errorStyles}>{errors.fourEventsPerYear}</div>}
          </div>

          <div css={fieldTopStyles}>
            <label css={labelStyles}>
              What kind of activities and events are you hoping to organize?
            </label>
            <TextArea
              id="activity-and-event"
              value={values.activityAndEvent}
              onChange={(e) => updateField('activityAndEvent', e.target.value)}
            />
            {errors?.activityAndEvent && <div css={errorStyles}>{errors.activityAndEvent}</div>}
          </div>
        </fieldset>
      );

    case 'community_content_creator':
      return (
        <fieldset css={sectionBoxStyles}>
          <legend css={legendLabelStyles}>
            Creating thought leadership or learning content
          </legend>

          <details css={detailsStyles}>
            <summary css={summaryStyles}>Am I eligible?</summary>
            <p css={detailsContentStyles}>
              To be eligible, you need to have shared at least two pieces of original Atlassian-focused thought
              leadership or learning content on social media each month in the last 90 days.
            </p>
          </details>

          <div css={fieldStyles}>
            <label css={labelStyles}>
              Why do you create Atlassian-focused content on social media?
            </label>
            <TextArea
              id="why-create-content"
              value={values.whyToCreateContent}
              onChange={(e) => updateField('whyToCreateContent', e.target.value)}
            />
            {errors?.whyToCreateContent && <div css={errorStyles}>{errors.whyToCreateContent}</div>}
          </div>

          <div css={fieldTopStyles}>
            {renderSelect({
              id: 'content-sharing-frequency',
              label: 'How often do you currently share your own content about Atlassian?',
              field: 'contentSharingFrequency',
              value: values.contentSharingFrequency,
              options: [
                { value: '', label: 'Select one' },
                { value: 'less_than_once_a_month', label: 'Less than once a month' },
                { value: 'once_a_month', label: 'Once a month' },
                { value: 'every_two_weeks', label: 'Every two weeks' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'multiple_times_a_week', label: 'Multiple times a week' },
              ],
              error: errors?.contentSharingFrequency,
            })}
          </div>

          <div css={fieldTopStyles}>
            <div css={fieldStyles}>
              <label css={labelStyles} htmlFor="best-content-1">
                Share three examples of original content featuring Atlassian products (Link 1)
              </label>
              <Textfield
                id="best-content-1"
                value={values.bestContent1}
                onChange={(e) => updateField('bestContent1', (e.target as HTMLInputElement).value)}
              />
              {errors?.bestContent1 && <div css={errorStyles}>{errors.bestContent1}</div>}
            </div>
            <div css={fieldSmallTopStyles}>
              <label css={labelStyles} htmlFor="best-content-2">Link 2</label>
              <Textfield
                id="best-content-2"
                value={values.bestContent2}
                onChange={(e) => updateField('bestContent2', (e.target as HTMLInputElement).value)}
              />
              {errors?.bestContent2 && <div css={errorStyles}>{errors.bestContent2}</div>}
            </div>
            <div css={fieldSmallTopStyles}>
              <label css={labelStyles} htmlFor="best-content-3">Link 3</label>
              <Textfield
                id="best-content-3"
                value={values.bestContent3}
                onChange={(e) => updateField('bestContent3', (e.target as HTMLInputElement).value)}
              />
              {errors?.bestContent3 && <div css={errorStyles}>{errors.bestContent3}</div>}
            </div>
          </div>

          <div css={fieldTopStyles}>
            <div css={fieldStyles}>
              <label css={labelStyles} htmlFor="primary-social-profile">
                Primary social media profile URL
              </label>
              <Textfield
                id="primary-social-profile"
                value={values.primarySocialProfile}
                onChange={(e) => updateField('primarySocialProfile', (e.target as HTMLInputElement).value)}
              />
              {errors?.primarySocialProfile && <div css={errorStyles}>{errors.primarySocialProfile}</div>}
            </div>
            <div css={fieldSmallTopStyles}>
              <label css={labelStyles} htmlFor="other-urls">
                Other social media profiles (optional)
              </label>
              <Textfield
                id="other-urls"
                value={values.otherURL}
                onChange={(e) => updateField('otherURL', (e.target as HTMLInputElement).value)}
              />
            </div>
          </div>

          <div css={fieldTopStyles}>
            <label css={labelStyles}>Can you commit to sharing 2 pieces of content per month?</label>
            {renderYesNoRadios('commitSharingContent', values.commitSharingContent)}
            {errors?.commitSharingContent && <div css={errorStyles}>{errors.commitSharingContent}</div>}
          </div>
        </fieldset>
      );

    case 'success_training_builder':
      return (
        <fieldset css={sectionBoxStyles}>
          <legend css={legendLabelStyles}>
            Creating or delivering Atlassian training for end users
          </legend>

          <div css={controlTopStyles}>
            {renderSelect({
              id: 'training-frequency',
              label: 'How often do you build training on Atlassian for end users?',
              field: 'trainingFrequency',
              value: values.trainingFrequency,
              options: [
                { value: '', label: 'Select one' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'yearly', label: 'Yearly' },
              ],
              error: errors?.trainingFrequency,
            })}
          </div>

          {renderTrainingSources()}
        </fieldset>
      );

    case 'success_cug_leader':
      return (
        <fieldset css={sectionBoxStyles}>
          <legend css={legendLabelStyles}>
            Running an Atlassian-focused Company User Group
          </legend>

          <div css={fieldTopStyles}>
            <label css={labelStyles}>
              Do you currently have an active Atlassian focused Company User Group or similar?
            </label>
            {renderYesNoUnsureRadios('activeCug', values.activeCug)}
            {errors?.activeCug && <div css={errorStyles}>{errors.activeCug}</div>}
          </div>

          <div css={fieldTopStyles}>
            {renderSelect({
              id: 'events-per-year',
              label: 'How many Company User Group sessions or other training events do you run each year?',
              field: 'eventsPerYear',
              value: values.eventsPerYear,
              options: [
                { value: '', label: 'Select one' },
                { value: 'up_to_4', label: 'Up to 4' },
                { value: '5_to_9', label: '5\u20139' },
                { value: '10_plus', label: '10 or more' },
              ],
              error: errors?.eventsPerYear,
            })}
          </div>
        </fieldset>
      );

    case 'success_coe_contributor':
      return (
        <fieldset css={sectionBoxStyles}>
          <legend css={legendLabelStyles}>
            Contributing to a Center of Excellence on Atlassian topics
          </legend>

          <div css={fieldTopStyles}>
            <label css={labelStyles}>
              Do you currently discuss Atlassian in a Center of Excellence?
            </label>
            {renderYesNoRadios('discussInCoE', values.discussInCoE)}
            {errors?.discussInCoE && <div css={errorStyles}>{errors.discussInCoE}</div>}
          </div>

          <div css={fieldTopStyles}>
            {renderSelect({
              id: 'coe-focus',
              label: "What's the focus of your Center of Excellence?",
              field: 'coeFocus',
              value: values.coeFocus,
              options: [
                { value: '', label: 'Select one' },
                { value: 'project_management', label: 'Project management' },
                { value: 'agile_methodologies', label: 'Agile methodologies' },
                { value: 'other', label: 'Other' },
              ],
              error: errors?.coeFocus,
            })}
          </div>

          {values.coeFocus === 'other' && (
            <div css={fieldTopStyles}>
              <label css={labelStyles} htmlFor="other-excellence">
                Other
              </label>
              <Textfield
                id="other-excellence"
                value={values.otherExcellence}
                maxLength={100}
                onChange={(e) => updateField('otherExcellence', (e.target as HTMLInputElement).value)}
              />
              {errors?.otherExcellence && <div css={errorStyles}>{errors.otherExcellence}</div>}
            </div>
          )}
        </fieldset>
      );

    default:
      return null;
  }
};

const sectionBoxStyles = css({
  border: `1px solid ${token('color.border')}`,
  borderRadius: '4px',
  padding: token('space.300'),
  marginBottom: token('space.400'),
  backgroundColor: token('elevation.surface'),
});

const legendLabelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
});

const detailsStyles = css({
  marginTop: token('space.100'),
  marginBottom: token('space.200'),
});

const summaryStyles = css({
  cursor: 'pointer',
  color: token('color.link'),
  fontWeight: 500,
});

const detailsContentStyles = css({
  marginTop: token('space.100'),
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.200'),
});

const fieldSmallTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.100'),
});

const controlTopStyles = css({
  marginTop: token('space.100'),
});

const labelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
  marginBottom: token('space.050'),
});

const listStyles = css({
  listStyleType: 'disc',
  paddingLeft: token('space.300'),
  marginTop: token('space.050'),
});

const errorStyles = css({
  color: token('color.text.danger'),
  fontSize: '12px',
  marginTop: token('space.050'),
});
